import { v4 as uuid } from 'uuid';
import { IdentityErrorCode, preAuthorizedScopes } from '../models/identity';
import Settings from '../../settings/settings';
import Routes from '../../utils/routes';
import { trackTrace, trackDependency } from '../../utils/telemetry/telemetry-channel';
import { Storage } from '../../utils/storage';
import { MlClientError, FailureOperation } from '../ml-client-error';
import { getLtiToken, getNonce as getLtiNonce, getState as getLtiState, setLtiItems } from './lti-provider';
import { InteractionRequiredAuthError, PublicClientApplication, } from '@azure/msal-browser';
const userCanceledErrorMessage = "Cannot read property 'indexOf' of undefined";
const authorityBase = Settings.AadAuthority;
const ambiguousTenantId = 'organizations';
const crossTenantAuthority = `${authorityBase}/${ambiguousTenantId}/`;
const returnUrlStorageKey = 'return_url';
const loginRequiredAttemptKey = 'login_required_attempt';
let isInitialized = false;
let storageType;
let context;
let authRedirectError;
let redirectPromise;
export async function init() {
    if (!isInitialized) {
        storageType = Storage.getStorageType();
        trackTrace(`MsalProvider: storageType = ${storageType}`);
        const configuration = {
            auth: {
                clientId: Settings.AadAppClientId,
                authority: crossTenantAuthority,
                redirectUri: `https://${window.location.host}${Routes.SignInReturnSpa}`,
                postLogoutRedirectUri: `https://${window.location.host}${Routes.LoggedOut}`,
            },
            cache: {
                cacheLocation: storageType,
            },
        };
        context = new PublicClientApplication(configuration);
        await context.initialize();
        redirectPromise = context
            .handleRedirectPromise({ navigateToLoginRequestUrl: false })
            .then((result) => {
            if (result) {
                context.setActiveAccount(result.account);
            }
            const wasLoginRequiredAttempt = hasLoginRequiredAttemptState();
            // clearing login required attempt state because we received MSAL hash params without errors (we got a token back)
            clearLoginRequiredAttemptState();
            trackTrace(`MsalProvider: handleRedirectPromise - no error received in hash params, wasLoginRequiredAttempt = ${wasLoginRequiredAttempt}`);
            return result;
        })
            .catch((authError) => {
            const wasLoginRequiredAttempt = hasLoginRequiredAttemptState();
            authRedirectError = authError;
            trackTrace(`MsalProvider: handleRedirectPromise - authRedirectError = ${JSON.stringify(authRedirectError)}, wasLoginRequiredAttempt = ${wasLoginRequiredAttempt}`);
            return authError;
        });
        isInitialized = true;
    }
}
function resolveErrorCode(authError) {
    let errorCode = authError && authError.errorCode
        ? authError.errorCode.toLowerCase()
        : IdentityErrorCode.Unknown;
    // HACK: MSA does not set any hash params when the user hits back from the login page
    // which results in the error message below being returned from MSAL
    if (errorCode === IdentityErrorCode.Unknown &&
        authError &&
        authError.message &&
        authError.message.trim().toLowerCase() === userCanceledErrorMessage.toLowerCase()) {
        errorCode = IdentityErrorCode.UserCanceled;
    }
    return errorCode;
}
export function clearUserSession() {
    if (isInitialized && context) {
        context.clearCache();
    }
    const ltiState = getLtiState();
    const ltiNonce = getLtiNonce();
    const ltiToken = getLtiToken();
    Storage.clear();
    // we should not clear lti items when signing out aad account
    if (!!ltiState && !!ltiNonce && !!ltiToken) {
        setLtiItems(ltiState, ltiNonce, ltiToken);
    }
}
export function clearCacheForScope(_accessToken) {
    // In MSAL v5, context.clearCache() wipes ALL tokens and accounts,
    // effectively logging the user out. The original intent was to force
    // a fresh token on the next acquireTokenSilent call, but MSAL v5
    // handles token refresh automatically via refresh tokens, so this
    // is no longer needed.
}
function setReturnUrl(returnUrl) {
    Storage.setItem(returnUrlStorageKey, returnUrl);
}
function setLoginRequiredAttemptState() {
    Storage.setItem(loginRequiredAttemptKey, 'true');
}
function clearLoginRequiredAttemptState() {
    Storage.removeItem(loginRequiredAttemptKey);
}
function hasLoginRequiredAttemptState() {
    const state = Storage.getItem(loginRequiredAttemptKey);
    return !!state;
}
export function getRedirectAuthError() {
    // resetting this state since it is no longer useful after this error check runs
    const wasLoginRequiredAttempt = hasLoginRequiredAttemptState();
    const authError = authRedirectError;
    authRedirectError = undefined;
    clearLoginRequiredAttemptState();
    if (!!authError) {
        const errorCode = resolveErrorCode(authError);
        const message = authError.message || 'An auth error occurred on redirect.';
        return {
            errorCode,
            message,
        };
    }
    if (wasLoginRequiredAttempt) {
        return {
            errorCode: IdentityErrorCode.LoginRequired,
            message: 'Failed to acquire an access token after a login required error and no error was returned from MSAL.',
        };
    }
    return undefined;
}
export function getReturnUrl() {
    if (!isInitialized) {
        throw new MlClientError('MsalProvider: The provider must be initialized before use.', '', FailureOperation.MsalProviderInit);
    }
    const returnUrl = Storage.getItem(returnUrlStorageKey);
    return returnUrl;
}
export async function loginSilent(tenantId) {
    if (!isInitialized || !redirectPromise) {
        throw new MlClientError('MsalProvider: The provider must be initialized before use.', '', FailureOperation.MsalProviderInit);
    }
    // Wait for the redirect promise to resolve before checking if the user is logged in
    await redirectPromise;
    const account = getAccount();
    if (!account) {
        return {
            isAuthenticated: false,
            errorCode: IdentityErrorCode.LoginRequired,
        };
    }
    if (!!tenantId) {
        const tid = account.idTokenClaims ? account.idTokenClaims['tid'] : undefined;
        if (tid !== tenantId) {
            clearUserSession();
            return {
                isAuthenticated: false,
                errorCode: IdentityErrorCode.LoginRequired,
            };
        }
    }
    return {
        isAuthenticated: true,
        account,
    };
}
export function loginRedirect(returnUrl, tenantId, loginHint, showPrompt = true) {
    if (!isInitialized) {
        throw new MlClientError('MsalProvider: The provider must be initialized before use.', '', FailureOperation.MsalProviderInit);
    }
    clearUserSession();
    if (returnUrl) {
        setReturnUrl(returnUrl);
    }
    const authority = !!tenantId ? `${authorityBase}/${tenantId}/` : crossTenantAuthority;
    const request = {
        scopes: [...preAuthorizedScopes],
        prompt: showPrompt ? 'select_account' : undefined,
        authority,
        loginHint,
    };
    context.loginRedirect(request);
}
export function loginPopup(tenantId, loginHint, showPrompt = true) {
    if (!isInitialized) {
        throw new MlClientError('MsalProvider: The provider must be initialized before use.', '', FailureOperation.MsalProviderInit);
    }
    clearUserSession();
    const authority = !!tenantId ? `${authorityBase}/${tenantId}/` : crossTenantAuthority;
    const request = {
        scopes: [...preAuthorizedScopes],
        prompt: showPrompt ? 'select_account' : undefined,
        authority,
        loginHint,
    };
    const result = context.loginPopup(request);
    return result;
}
export function logOut() {
    if (!isInitialized) {
        throw new MlClientError('MsalProvider: The provider must be initialized before use.', '', FailureOperation.MsalProviderInit);
    }
    clearUserSession();
    context.logoutRedirect();
}
export async function getAccessToken(scopes, tenantId, returnUrl, shouldOpenPopupWindow = false) {
    if (!isInitialized || !redirectPromise) {
        throw new MlClientError('MsalProvider: The provider must be initialized before use.', '', FailureOperation.MsalProviderInit);
    }
    const correlationId = uuid();
    const authority = `${authorityBase}/${tenantId}/`;
    // Wait for the redirect promise to resolve before checking if the user is logged in
    await redirectPromise;
    const account = getAccount();
    // Set the tenantId in the account object to the provided tenantId if it is different
    // to ensure that the correct tenantId is used when acquiring tokens. Note: due to the
    // way caching is handled in MSAL, the token will not be cached for the new tenantId.
    // All calls to acquireTokenSilent with the new tenantId will result in a call to Entra
    // to get a new token.
    // This is a workaround for the fact that MSAL v2 does not use the authority in the cache
    // key; without this, the call to acquireTokenSilent would pull the token from the cache
    // the was generated for the previous tenantId.
    if (!!account?.tenantId && tenantId !== account.tenantId) {
        account.tenantId = tenantId;
    }
    const request = {
        scopes: [...scopes],
        authority,
        account,
        correlationId,
    };
    // for dependency tracking
    const startTime = new Date().getTime();
    // When running inside an LMS iframe (Canvas, Moodle, etc.), acquireTokenSilent
    // creates a hidden iframe to perform silent auth. In MSAL v5, this nested iframe
    // cannot complete the token exchange due to browser cross-origin iframe sandboxing
    // restrictions. Skip silent auth and go directly to popup when shouldOpenPopupWindow
    // is true (i.e., the caller is an LMS host).
    if (shouldOpenPopupWindow && account) {
        try {
            const result = await context.acquireTokenPopup(request);
            const endTime = new Date().getTime();
            const duration = endTime - startTime;
            const { accessToken } = result;
            trackDependency(correlationId, 200, 'MsalProvider-getAccessTokenPopup', duration, true);
            return {
                accessToken,
                isRedirecting: false,
            };
        }
        catch (error) {
            const endTime = new Date().getTime();
            const duration = endTime - startTime;
            const authError = error;
            const errorCode = resolveErrorCode(authError);
            // Adjust tracking based on specific error codes
            switch (errorCode) {
                case IdentityErrorCode.LoginRequired:
                case IdentityErrorCode.InteractionRequired:
                    trackDependency(correlationId, 300, 'MsalProvider-getAccessTokenPopup', duration, true);
                    break;
                case IdentityErrorCode.InvalidState:
                    trackDependency(correlationId, 400, 'MsalProvider-getAccessTokenPopup', duration, true);
                    break;
                default:
                    trackDependency(correlationId, 500, 'MsalProvider-getAccessTokenPopup', duration, false);
                    break;
            }
            return {
                isRedirecting: false,
                errorCode,
            };
        }
    }
    try {
        const result = await context.acquireTokenSilent(request);
        const endTime = new Date().getTime();
        const duration = endTime - startTime;
        // this shouldn't happen, but if it does we want to detect the MSAL bug
        if (!result || !result.accessToken) {
            trackDependency(correlationId, 500, 'MsalProvider-getAccessToken', duration, false);
            return {
                isRedirecting: false,
                errorCode: IdentityErrorCode.Unknown,
            };
        }
        // only recording the dependency when it isn't a cached result because this gets called
        // a lot
        const { accessToken, fromCache } = result;
        if (!fromCache) {
            trackDependency(correlationId, 200, 'MsalProvider-getAccessToken', duration, true);
        }
        return {
            accessToken,
            isRedirecting: false,
        };
    }
    catch (error) {
        const endTime = new Date().getTime();
        const duration = endTime - startTime;
        const authError = error;
        let errorCode = resolveErrorCode(authError);
        // In MSAL v5, InteractionRequiredAuthError may carry server error codes
        // (e.g. invalid_grant, bad_token) that resolveErrorCode doesn't map to a
        // known IdentityErrorCode. Remap to InteractionRequired only when the code
        // isn't already one with specific handling (e.g. LoginRequired sets attempt state).
        if (error instanceof InteractionRequiredAuthError) {
            switch (errorCode) {
                case IdentityErrorCode.LoginRequired:
                case IdentityErrorCode.Renewal:
                case IdentityErrorCode.InteractionRequired:
                case IdentityErrorCode.UserLogin:
                    break;
                default:
                    errorCode = IdentityErrorCode.InteractionRequired;
                    break;
            }
        }
        // some error codes are expected, so we can consider those successful dependencies
        switch (errorCode) {
            case IdentityErrorCode.LoginRequired:
            case IdentityErrorCode.Renewal:
            case IdentityErrorCode.InteractionRequired:
            case IdentityErrorCode.UserLogin:
                trackDependency(correlationId, 300, 'MsalProvider-getAccessToken', duration, true);
                break;
            case IdentityErrorCode.InvalidState:
                trackDependency(correlationId, 400, 'MsalProvider-getAccessToken', duration, true);
                break;
            default:
                trackDependency(correlationId, 500, 'MsalProvider-getAccessToken', duration, false);
                break;
        }
        // if we can redirect or open popup, lets kick it off for the error codes that require it
        if (returnUrl || shouldOpenPopupWindow) {
            const startTime = new Date().getTime();
            switch (errorCode) {
                case IdentityErrorCode.LoginRequired:
                    // setting state so we can record that we are attempting a full
                    // frame redirect for an access token when hitting a login
                    // required error
                    if (!shouldOpenPopupWindow) {
                        setLoginRequiredAttemptState();
                    }
                case IdentityErrorCode.Renewal:
                case IdentityErrorCode.InteractionRequired:
                    if (returnUrl) {
                        setReturnUrl(returnUrl);
                        context.acquireTokenRedirect({ ...request });
                        return {
                            isRedirecting: true,
                        };
                    }
                    else {
                        try {
                            const result = await context.acquireTokenPopup(request);
                            const endTime = new Date().getTime();
                            const duration = endTime - startTime;
                            const { accessToken } = result;
                            trackDependency(correlationId, 200, 'MsalProvider-getAccessTokenPopup', duration, true);
                            return {
                                accessToken,
                                isRedirecting: false,
                            };
                        }
                        catch (error) {
                            const authError = error;
                            const errorCode = resolveErrorCode(authError);
                            return {
                                isRedirecting: false,
                                errorCode,
                            };
                        }
                    }
                case IdentityErrorCode.UserLogin:
                    if (returnUrl) {
                        loginRedirect(returnUrl);
                        return {
                            isRedirecting: true,
                        };
                    }
                    else {
                        try {
                            const result = await loginPopup();
                            const endTime = new Date().getTime();
                            const duration = endTime - startTime;
                            const { accessToken } = result;
                            trackDependency(correlationId, 200, 'MsalProvider-getAccessTokenPopup', duration, true);
                            return {
                                accessToken,
                                isRedirecting: false,
                            };
                        }
                        catch (error) {
                            const authError = error;
                            const errorCode = resolveErrorCode(authError);
                            return {
                                isRedirecting: false,
                                errorCode,
                            };
                        }
                    }
                default:
                    break;
            }
        }
        return {
            isRedirecting: false,
            errorCode,
        };
    }
}
export function isMsalIframe() {
    // this code was adapted from the MSAl WindowUtils to replicate
    // functionality that is no longer intended to be exported as of
    // MSAL 1.2.0, see: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-core/src/utils/WindowUtils.ts
    if (window.parent !== window) {
        try {
            return window.parent.location.hostname === window.location.hostname;
        }
        catch (err) {
            // blocked from accessing cross origin iframe info so we know the origin is not the same as our own
            return false;
        }
    }
    return false;
}
export function isMsalPopup() {
    // this code was adapted from the MSAl WindowUtils to replicate
    // functionality that is no longer intended to be exported as of
    // MSAL 1.2.0, see: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-core/src/utils/WindowUtils.ts
    if (!!(window.opener && window.opener !== window)) {
        try {
            // Only treat this as an MSAL popup if the opener is same-origin.
            // Canvas LTI (and other LMS platforms) open our page in a popup
            // from a different origin — those are NOT MSAL auth popups and
            // must proceed with normal app initialization.
            if (window.opener.location.origin !== window.location.origin) {
                return false;
            }
            // if the opener origin is the same as our own origin and it is not for Teams MFA flow
            const urlParams = new URLSearchParams(window.opener.location.search);
            if (urlParams.get('host') === 'Teams') {
                return false;
            }
            return true;
        }
        catch (err) {
            // blocked from accessing cross origin info so we know the origin is not the same as our own
            return false;
        }
    }
    return false;
}
function getAccount() {
    if (!context) {
        return undefined;
    }
    const account = context.getActiveAccount();
    if (account) {
        return account;
    }
    const accounts = context.getAllAccounts();
    if (accounts.length > 0) {
        return accounts[0];
    }
    return undefined;
}
const MsalProvider = {
    init,
    getReturnUrl,
    loginSilent,
    loginRedirect,
    loginPopup,
    logOut,
    getAccessToken,
    isMsalIframe,
    clearCacheForScope,
    clearUserSession,
    isMsalPopup,
};
export default MsalProvider;
